import { FunctionComponent } from "react";

import { DATE_FORMATS, DateTime } from "@bps/date-time";
import { DefaultButton, Spinner, Stack, Text } from "@bps/fluent-ui";
import { MfaMethodDto } from "@libs/api/gateways/bp-id/bp-id.dtos";
import { useRemoveMfaMethod } from "@libs/api/gateways/bp-id/bp-id.hooks";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import { Permissions } from "@libs/permissions/permissions.enum";

export interface MfaMethodProps {
  bpIdUserId?: string;
  method: MfaMethodDto;
  refresh: () => void;
}

export const MfaMethodDisplay: FunctionComponent<MfaMethodProps> = ({
  bpIdUserId,
  method,
  refresh
}) => {
  const { mutateAsync: removeMfaRequest, isLoading } = useRemoveMfaMethod();
  const confirmRemoveMfa = async () => {
    const result = confirm(
      `Are you sure you want to remove the MFA method "${method.name}" ?`
    );

    const mfaMethodMapping = {
      sms: "phone",
      email: "additionalEmail",
      authenticator: "totp"
    };

    const mfaMethodName = mfaMethodMapping[method.mfaMethod];
    if (result) {
      bpIdUserId &&
        (await removeMfaRequest({ bpIdUserId, mfaMethod: mfaMethodName }));
      refresh();
    }
  };

  const { data: hasDeletePermission } = usePermissionsCheckQuery(
    Permissions.PltBpidMfaDelete
  );

  const formatMfaMethodValue = (
    value?: string,
    updatedDateTime?: string
  ): string => {
    if (value && value.trim().length > 0) return value;
    if (!updatedDateTime) return "";

    return `Last set ${DateTime.fromISO(updatedDateTime, { zone: "utc" })
      .setZone(DateTime.now().timeZone)
      .toFormat(DATE_FORMATS.DAY_DEFAULT_FORMAT)}`;
  };

  const textStyle = { root: { marginTop: 8 } };
  return (
    <Stack horizontal tokens={{ childrenGap: 8 }}>
      <>
        <Text styles={textStyle} bold>
          {method.name}
        </Text>
        <Text styles={textStyle}>
          {formatMfaMethodValue(method.value, method.updatedDateTime)}
        </Text>
      </>
      {hasDeletePermission && (
        <DefaultButton styles={{}} onClick={confirmRemoveMfa}>
          {isLoading ? <Spinner /> : "Remove Method"}
        </DefaultButton>
      )}
    </Stack>
  );
};
